// ExceptionBanner — the GLOBAL exception banner promoted above the hero.
//
// Derived renderer-side (GAP-8): DARK feed OR a non-zero hard safety/alarm
// figure. camera_inventory is a card-level WATCH (see CameraWatchBanner), NEVER
// this global promotion. Renders nothing when not promoted.
import React from "react";

import type { ExceptionBannerResolution } from "./honestyEngine";
import useTranslation from "../../../hooks/useTranslation";
import { dt } from "../../../themes/redesignTokens";
import { VERDICT_TONE_COLORS } from "./honestyTokens";

export interface ExceptionBannerProps {
  resolution: ExceptionBannerResolution;
}

const REASON_TEXT: Record<string, string> = {
  dark_feed: "Feed is dark — live values unavailable, counts frozen",
  safety_alarm: "Safety / alarm active this window",
};

const REASON_KEY: Record<string, string> = {
  dark_feed: "exception.darkFeed",
  safety_alarm: "exception.safetyAlarm",
};

export const ExceptionBanner: React.FC<ExceptionBannerProps> = ({ resolution }) => {
  const { translate } = useTranslation("machineEvidence");
  if (!resolution.promote) return null;
  const tone = VERDICT_TONE_COLORS[resolution.level === "critical" ? "critical" : "warning"];
  const text = resolution.reason
    ? (translate(REASON_KEY[resolution.reason], { defaultValue: REASON_TEXT[resolution.reason] }) as string)
    : (translate("exception.attention", { defaultValue: "Attention" }) as string);

  return (
    <div
      className={`msc-exception msc-exception-${resolution.level}`}
      role="alert"
      data-banner-reason={resolution.reason ?? ""}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderRadius: dt.radiusSm,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.fg,
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      <span aria-hidden style={{ fontWeight: 700 }}>
        !
      </span>
      <span>{text}</span>
    </div>
  );
};

/** Card-level camera WATCH (amber) — Vision card ONLY. Distinct from the global
 *  banner: a camera drop is a watch, never a machine alarm. */
export const CameraWatchBanner: React.FC<{ show: boolean }> = ({ show }) => {
  const { translate } = useTranslation("machineEvidence");
  if (!show) return null;
  const tone = VERDICT_TONE_COLORS.warning;
  return (
    <div
      className="msc-camera-watch"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        borderRadius: dt.radiusSm,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.fg,
        fontSize: 12,
      }}
    >
      <span aria-hidden>◇</span>
      <span>
        {translate("cameraWatch", {
          defaultValue: "Camera inventory watch — reduced inspection coverage",
        }) as string}
      </span>
    </div>
  );
};

export default ExceptionBanner;
